import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MenuItem } from 'primeng/api';

import { OAuthApplicationsService } from '@generated/services/o-auth-applications.service';
import { OAuthApplicationDto, CreateOAuthApplicationCommand, UpdateOAuthApplicationCommand } from '@generated/models';
import { NotificationService } from '@core/services/notification.service';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { OAUTH_APP_FIELDS } from '@core/assistant/assistant-catalog';

type Mode = 'form' | 'secret';

@Component({
  selector: 'app-oauth-application-edit',
  standalone: true,
  imports: [
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantActionDirective,
    FormsModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    ProgressSpinnerModule,
    TooltipModule,
    MessageModule,
    BreadcrumbModule,
    DeleteConfirmationComponent
],
  templateUrl: './oauth-application-edit.component.html',
  styleUrl: './oauth-application-edit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OAuthApplicationEditComponent implements OnInit {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = OAUTH_APP_FIELDS;

  private oauthService = inject(OAuthApplicationsService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  applicationId: string | null = null;
  isEditMode = false;
  isLoading = false;
  isSaving = false;
  isRegenerating = false;
  isDeleting = false;

  mode: Mode = 'form';

  // Form state
  name = '';
  redirectUris: string[] = [''];

  // Secret-once state
  clientIdShown = '';
  clientSecretShown = '';

  deleteDialogVisible = false;
  regenerateDialogVisible = false;

  readonly saveBtnCreateLabel = $localize`:@@oauthApps.create.button:Create`;
  readonly saveBtnEditLabel = $localize`:@@oauthApps.save.button:Save`;

  readonly deleteTitle = $localize`:@@oauthApps.delete.title:Delete application`;
  readonly deleteSubtitle = $localize`:@@oauthApps.delete.subtitle:Are you sure? This cannot be undone, the client will be deleted in Keycloak.`;
  readonly regenerateTitle = $localize`:@@oauthApps.regenerate.title:Generate new secret`;
  readonly regenerateSubtitle = $localize`:@@oauthApps.regenerate.subtitle:The old secret will stop working immediately. Any integration using the old secret will break.`;

  breadcrumbItems: MenuItem[] = [];

  ngOnInit(): void {
    this.applicationId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.applicationId;
    this.buildBreadcrumb();
    if (this.isEditMode && this.applicationId) {
      this.loadApplication(this.applicationId);
    }
  }

  private buildBreadcrumb(): void {
    this.breadcrumbItems = [
      { label: $localize`:@@developers.title:Developers`, routerLink: ['/developers'] },
      { label: $localize`:@@oauthApps.tabName:OAuth Applications`, routerLink: ['/developers'] },
      {
        label: this.isEditMode
          ? $localize`:@@oauthApps.editTitle:Edit application`
          : $localize`:@@oauthApps.newTitle:New application`
      }
    ];
  }

  private loadApplication(id: string): void {
    this.isLoading = true;
    this.oauthService.apiOAuthApplicationsIdGet({ id }).subscribe({
      next: (app: OAuthApplicationDto) => {
        this.name = app.name || '';
        this.redirectUris = app.redirect_uris && app.redirect_uris.length > 0 ? [...app.redirect_uris] : [''];
        this.clientIdShown = app.client_id || '';
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notificationService.showError($localize`:@@oauthApps.notify.loadFail:Failed to load application`);
        this.isLoading = false;
        this.router.navigate(['/developers']);
        this.cdr.markForCheck();
      }
    });
  }

  get headerTitle(): string {
    return this.isEditMode
      ? $localize`:@@oauthApps.editTitle:Edit application`
      : $localize`:@@oauthApps.newTitle:New application`;
  }

  addRedirectUri(): void {
    this.redirectUris = [...this.redirectUris, ''];
    this.cdr.markForCheck();
  }

  removeRedirectUri(index: number): void {
    if (this.redirectUris.length === 1) {
      this.redirectUris = [''];
    } else {
      this.redirectUris = this.redirectUris.filter((_, i) => i !== index);
    }
    this.cdr.markForCheck();
  }

  trackByIndex(index: number): number {
    return index;
  }

  get canSave(): boolean {
    return !this.isSaving && this.name.trim().length > 0 &&
      this.redirectUris.some(u => u.trim().length > 0);
  }

  private validUris(): string[] {
    return this.redirectUris.map(u => u.trim()).filter(u => u.length > 0);
  }

  save(): void {
    if (!this.canSave) return;
    this.isSaving = true;
    this.cdr.markForCheck();

    if (this.isEditMode && this.applicationId) {
      const command: UpdateOAuthApplicationCommand = {
        id: this.applicationId,
        name: this.name.trim(),
        redirect_uris: this.validUris()
      };
      this.oauthService.apiOAuthApplicationsIdPut({ id: this.applicationId, body: command }).subscribe({
        next: () => {
          this.notificationService.showSuccess($localize`:@@oauthApps.notify.saved:Application saved`);
          this.isSaving = false;
          this.cdr.markForCheck();
          this.router.navigate(['/developers']);
        },
        error: () => {
          this.notificationService.showError($localize`:@@oauthApps.notify.saveFail:Failed to save application`);
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
    } else {
      const command: CreateOAuthApplicationCommand = {
        name: this.name.trim(),
        redirect_uris: this.validUris()
      };
      this.oauthService.apiOAuthApplicationsPost({ body: command }).subscribe({
        next: (result) => {
          this.notificationService.showSuccess($localize`:@@oauthApps.notify.created:Application created`);
          this.applicationId = result.id || null;
          this.isEditMode = true;
          this.buildBreadcrumb();
          this.clientIdShown = result.client_id || '';
          this.clientSecretShown = result.client_secret || '';
          this.mode = 'secret';
          this.isSaving = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.notificationService.showError($localize`:@@oauthApps.notify.createFail:Failed to create application`);
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
    }
  }

  askRegenerate(): void {
    this.regenerateDialogVisible = true;
    this.cdr.markForCheck();
  }

  onRegenerateConfirm(): void {
    if (!this.applicationId) return;
    this.isRegenerating = true;
    this.cdr.markForCheck();

    this.oauthService.apiOAuthApplicationsIdRegenerateSecretPost({ id: this.applicationId }).subscribe({
      next: (result) => {
        this.notificationService.showSuccess($localize`:@@oauthApps.notify.regenerated:Secret regenerated`);
        this.clientIdShown = result.client_id || this.clientIdShown;
        this.clientSecretShown = result.client_secret || '';
        this.mode = 'secret';
        this.isRegenerating = false;
        this.regenerateDialogVisible = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notificationService.showError($localize`:@@oauthApps.notify.regenerateFail:Failed to regenerate secret`);
        this.isRegenerating = false;
        this.regenerateDialogVisible = false;
        this.cdr.markForCheck();
      }
    });
  }

  onRegenerateCancel(): void {
    this.regenerateDialogVisible = false;
    this.cdr.markForCheck();
  }

  askDelete(): void {
    this.deleteDialogVisible = true;
    this.cdr.markForCheck();
  }

  onDeleteConfirm(): void {
    if (!this.applicationId) return;
    this.isDeleting = true;
    this.cdr.markForCheck();

    this.oauthService.apiOAuthApplicationsIdDelete({ id: this.applicationId }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@oauthApps.notify.deleted:Application deleted`);
        this.isDeleting = false;
        this.deleteDialogVisible = false;
        this.cdr.markForCheck();
        this.router.navigate(['/developers']);
      },
      error: () => {
        this.notificationService.showError($localize`:@@oauthApps.notify.deleteFail:Не удалось удалить приложение`);
        this.isDeleting = false;
        this.deleteDialogVisible = false;
        this.cdr.markForCheck();
      }
    });
  }

  onDeleteCancel(): void {
    this.deleteDialogVisible = false;
    this.cdr.markForCheck();
  }

  async copy(value: string, label: string): Promise<void> {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      this.notificationService.showSuccess($localize`:@@oauthApps.notify.copied:${label}:label: скопирован`);
    } catch {
      this.notificationService.showError($localize`:@@oauthApps.notify.copyFail:Не удалось скопировать`);
    }
  }

  goBack(): void {
    this.router.navigate(['/developers']);
  }
}
